import React, { useMemo } from "react";
import { MdTrendingUp, MdTrendingDown, MdAttachMoney, MdWork, MdWarning } from "react-icons/md";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(n || 0);

const StatusBadge = ({ status }) => {
  const colors = { Ongoing: ["#E8F5E9", "#2E7D32"], Delayed: ["#FFF8E1", "#E65100"], Stopped: ["#FFEBEE", "#B71C1C"] };
  const [bg, text] = colors[status] || colors.Ongoing;
  return (
    <span style={{ background: bg, color: text, borderRadius: "999px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>{status}</span>
  );
};

export default function ProfitabilityDashboard({ projects }) {
  const activeProjects = useMemo(() => projects.filter(p => p.status !== "Stopped"), [projects]);

  const totals = useMemo(() => {
    return activeProjects.reduce((acc, p) => ({
      contractValue: acc.contractValue + (p.totalContractValue || 0),
      laborCost:     acc.laborCost     + (p.laborCost || 0),
      externalCosts: acc.externalCosts + (p.externalCosts || 0),
      profit:        acc.profit        + (p.forecastedProfit || 0),
      commissions:   acc.commissions   + (p.referralRate && p.totalContractValue
                        ? (p.totalContractValue * p.referralRate / 100) : 0),
    }), { contractValue: 0, laborCost: 0, externalCosts: 0, profit: 0, commissions: 0 });
  }, [activeProjects]);

  const overallMargin = totals.contractValue > 0
    ? ((totals.profit / totals.contractValue) * 100).toFixed(1)
    : 0;

  const topProjects = useMemo(() =>
    [...activeProjects]
      .sort((a, b) => (b.forecastedProfit || 0) - (a.forecastedProfit || 0))
      .slice(0, 5),
  [activeProjects]);

  const atRiskProjects = useMemo(() =>
    projects.filter(p => p.status === "Delayed" || p.status === "Stopped" || p.forecastedProfit < 0),
  [projects]);

  return (
    <div style={{ padding: "0", display: "grid", gap: "16px" }}>

      {/* ── KPI Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
        {[
          { label: "Total Contract Value", value: fmt(totals.contractValue), icon: MdAttachMoney, color: "#3761E9", bg: "#EEF2FF" },
          { label: "Total Labor Cost",     value: fmt(totals.laborCost),     icon: MdWork,       color: "#E53935", bg: "#FFEBEE" },
          { label: "External Costs",        value: fmt(totals.externalCosts), icon: MdAttachMoney,color: "#FB8C00", bg: "#FFF8E1" },
          { label: "Forecasted Profit",     value: fmt(totals.profit),        icon: totals.profit >= 0 ? MdTrendingUp : MdTrendingDown,
            color: totals.profit >= 0 ? "#2E7D32" : "#B71C1C", bg: totals.profit >= 0 ? "#E8F5E9" : "#FFEBEE" },
        ].map(kpi => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} style={{ background: kpi.bg, borderRadius: "14px", padding: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <p style={{ fontSize: "10px", color: kpi.color, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.3 }}>{kpi.label}</p>
                <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: "8px", padding: "5px", display: "flex" }}>
                  <Icon size={16} color={kpi.color} />
                </div>
              </div>
              <p style={{ fontSize: "20px", fontWeight: 900, color: kpi.color, marginTop: "8px" }}>{kpi.value}</p>
            </div>
          );
        })}
      </div>

      {/* ── Overall Margin ── */}
      <div style={{ background: "linear-gradient(135deg, #0f3460, #1a1a2e)", borderRadius: "16px", padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.6px" }}>Portfolio Margin</p>
          <p style={{ fontSize: "38px", fontWeight: 900, color: overallMargin >= 30 ? "#6affb0" : overallMargin >= 15 ? "#ffd166" : "#ff6b6b" }}>{overallMargin}%</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)" }}>Active Projects</p>
          <p style={{ fontSize: "32px", fontWeight: 900, color: "#fff" }}>{activeProjects.length}</p>
          {totals.commissions > 0 && (
            <p style={{ fontSize: "11px", color: "#ffd166", marginTop: "4px" }}>🤝 Commissions: {fmt(totals.commissions)}</p>
          )}
        </div>
      </div>

      {/* ── Tables Row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>

        {/* Top Performing Projects */}
        <div style={{ background: "#fff", borderRadius: "14px", padding: "16px", border: "1px solid #e8e8f0" }}>
          <p style={{ fontSize: "12px", fontWeight: 700, color: "#1a1a2e", marginBottom: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
            <MdTrendingUp size={15} color="#2E7D32" /> Top Projects by Profit
          </p>
          {topProjects.length === 0
            ? <p style={{ fontSize: "12px", color: "#bbb", textAlign: "center", padding: "20px 0" }}>No projects yet</p>
            : topProjects.map((p, i) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < topProjects.length - 1 ? "1px solid #f0f0f8" : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ width: "20px", height: "20px", background: "#f0f4ff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, color: "#3761E9", flexShrink: 0 }}>
                    {i + 1}
                  </span>
                  <div>
                    <p style={{ fontSize: "11px", fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "150px" }}>{p.title}</p>
                    <p style={{ fontSize: "9px", color: "#aaa" }}>{p.clientName}</p>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: (p.forecastedProfit || 0) >= 0 ? "#2E7D32" : "#B71C1C" }}>{fmt(p.forecastedProfit)}</span>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))
          }
        </div>

        {/* At-Risk Projects */}
        <div style={{ background: "#fff", borderRadius: "14px", padding: "16px", border: "1px solid #e8e8f0" }}>
          <p style={{ fontSize: "12px", fontWeight: 700, color: "#1a1a2e", marginBottom: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
            <MdWarning size={15} color="#E65100" /> At-Risk Projects
          </p>
          {atRiskProjects.length === 0
            ? <p style={{ fontSize: "12px", color: "#bbb", textAlign: "center", padding: "20px 0" }}>✅ All projects are on track!</p>
            : atRiskProjects.map((p, i) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < atRiskProjects.length - 1 ? "1px solid #f0f0f8" : "none" }}>
                <div>
                  <p style={{ fontSize: "11px", fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "170px" }}>{p.title}</p>
                  <p style={{ fontSize: "9px", color: "#aaa" }}>{p.clientName}</p>
                </div>
                <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
                  <StatusBadge status={p.status} />
                  {p.forecastedProfit < 0 && (
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#B71C1C" }}>{fmt(p.forecastedProfit)}</span>
                  )}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}
